import { useEffect } from "react";

/**
 * Scroll reveal, reimplemented.
 *
 * The comp fades blocks up as they enter the viewport. That behavior lives in
 * `support.js`, Claude Design's own 1,911-line generated runtime, which the
 * handoff says not to port — read it to understand the behavior, then write the
 * behavior. This is that.
 *
 * Three properties carried over from the comp's version, each of which matters:
 *
 *  - **Anything already above the fold is never hidden.** The comp only arms an
 *    element whose top sits below 92% of the viewport height. Without that,
 *    the hero and the first section flash blank on load.
 *  - **`prefers-reduced-motion` disables it entirely**, and so does a missing
 *    IntersectionObserver.
 *  - **A safety timer reveals anything still hidden.** An element that never
 *    intersects — because it is inside a collapsed container, or the observer
 *    misfires — must not stay invisible. Content that cannot be read is a worse
 *    failure than content that appears without its animation.
 *
 * The class that hides elements is added by JS (`.bb-reveal-armed`), so a
 * visitor with no JavaScript gets the full page rather than an empty one.
 */
export function useReveal(key?: string) {
  useEffect(() => {
    const root = document.querySelector<HTMLElement>(".bluebird");
    if (!root) return;

    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const els = Array.from(root.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (reduced || !("IntersectionObserver" in window)) {
      els.forEach((el) => el.setAttribute("data-reveal-done", "1"));
      return;
    }

    root.classList.add("bb-reveal-armed");

    // Stagger: consecutive siblings marked data-reveal-stagger come in one
    // after another rather than all at once.
    const delayFor = (el: HTMLElement) => {
      const explicit = el.getAttribute("data-reveal-delay");
      if (explicit) return Number(explicit);
      if (!el.hasAttribute("data-reveal-stagger")) return 0;
      let i = 0;
      let prev = el.previousElementSibling as HTMLElement | null;
      while (prev?.hasAttribute?.("data-reveal-stagger")) {
        i += 1;
        prev = prev.previousElementSibling as HTMLElement | null;
      }
      return i * 90;
    };

    const reveal = (el: HTMLElement) => {
      window.setTimeout(() => el.setAttribute("data-reveal-done", "1"), delayFor(el));
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          // `isIntersecting` alone is not enough. A block TALLER than the
          // viewport can straddle it — top above, bottom below — and never
          // present the fraction `threshold` asks for, so the callback fires
          // with isIntersecting=false and the element stays hidden forever.
          //
          // This shipped: #visit is ~500px tall, and at threshold 0.06 with a
          // -6% root margin, landing with it partly on screen left it at
          // opacity 0 permanently. Reproduced 2 runs in 4 by jumping straight
          // to a scroll offset, which is what a restored scroll position, an
          // in-page anchor, or a fast flick does. Aaron hit it on real page
          // loads; the shop band and #visit rendered as empty blue.
          //
          // So: reveal if it intersects at all, OR if any part of it is simply
          // inside the viewport. The second test is the one that catches the
          // straddling case.
          const r = e.boundingClientRect;
          const onScreen = r.top < window.innerHeight && r.bottom > 0;
          if (!e.isIntersecting && !onScreen) continue;
          io.unobserve(e.target);
          reveal(e.target as HTMLElement);
        }
      },
      // No negative bottom margin, and a threshold of 0 rather than 0.06.
      // Both existed to delay the reveal until a block was meaningfully on
      // screen, and both are what made a tall block unreachable. A reveal that
      // fires slightly early is invisible; one that never fires is a blank page.
      { rootMargin: "0px", threshold: 0 }
    );

    const fold = (window.innerHeight || 800) * 0.92;
    for (const el of els) {
      if (el.getBoundingClientRect().top <= fold) {
        el.setAttribute("data-reveal-done", "1");
        continue;
      }
      io.observe(el);
    }

    // Second line of defence: anything that scrolls into view is revealed on
    // the next frame regardless of what the observer decided. The observer is
    // an optimisation; this is the guarantee. Content that cannot be read is a
    // worse failure than content that appears without its animation.
    const sweep = () => {
      let remaining = 0;
      for (const el of els) {
        if (el.hasAttribute("data-reveal-done")) continue;
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.setAttribute("data-reveal-done", "1");
        } else {
          remaining += 1;
        }
      }
      if (!remaining) {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      }
    };
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => { ticking = false; sweep(); });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    sweep();

    // Last resort: nothing stays hidden forever, whatever the observer and the
    // scroll sweep do.
    const failsafe = window.setTimeout(() => {
      els.forEach((el) => el.setAttribute("data-reveal-done", "1"));
    }, 4000);

    return () => {
      io.disconnect();
      window.clearTimeout(failsafe);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      root.classList.remove("bb-reveal-armed");
    };
  }, [key]);
}
